import React from 'react'
import { Copy, t } from '@stokr/components-library'

// Same escaping as server/app.js: neutralise </script> injection and
// JS line-terminator characters that are legal JSON but break string literals.
const LS = String.fromCharCode(0x2028)
const PS = String.fromCharCode(0x2029)
const safeJson = (data) =>
  JSON.stringify(data ?? {})
    .replace(/</g, '\\u003c')
    .replace(new RegExp(LS, 'g'), '\\u2028')
    .replace(new RegExp(PS, 'g'), '\\u2029')

/**
 * Full HTML document shell rendered by React.
 *
 * Used on the server (entry-server.jsx wraps the App tree with this) and on
 * the client (entry-client.jsx passes it to hydrateRoot so React owns the
 * full document). React 19 then hoists <title> / <meta> / <link> tags from
 * anywhere in the tree into <head> automatically — no react-helmet-async needed.
 *
 * Styled-components <style> tags and <link rel="modulepreload"> tags are
 * NOT rendered here. entry-server.jsx injects them via string replacement
 * into the buffered HTML just before </head>. React 19's "gentle head
 * hydration" leaves those server-inserted tags in place without warnings.
 *
 * ssrData must be the same object on server and client so the
 * window.__SSR_DATA__ script content matches and hydration succeeds.
 */
export const Document = ({ children, ssrData = {} }) => {
  const ssrDataStr = safeJson(ssrData)

  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <link rel="icon" type="image/png" href="/favicon.ico" />

        <link
          rel="stylesheet"
          href="https://fonts.googleapis.com/css?family=Open+Sans:300,300i,400,400i,600,600i,700,700i,800,800i&display=swap"
        />

        <link rel="stylesheet" type="text/css" href="//cdn.jsdelivr.net/gh/kenwheeler/slick@1.6.0/slick/slick.css" />
        <link
          rel="stylesheet"
          type="text/css"
          href="//cdn.jsdelivr.net/gh/kenwheeler/slick@1.6.0/slick/slick-theme.css"
        />

        <link rel="manifest" href="/manifest.json" crossOrigin="use-credentials" />

        <link rel="preconnect" href="https://res.cloudinary.com" crossOrigin="" />
        <link rel="dns-prefetch" href="https://res.cloudinary.com" />

        <meta name="viewport" content="width=device-width, initial-scale=1, shrink-to-fit=no, viewport-fit=cover" />
        <meta name="theme-color" content="#ee220d" />
        <meta name="referrer" content="strict-origin-when-cross-origin" />
        <meta name="keywords" content={t('document.keywords')} />

        <style>{`*, *::before, *::after { box-sizing: border-box; }`}</style>
      </head>
      <body>
        <noscript>
          <img
            height="1"
            width="1"
            style={{ display: 'none' }}
            src="https://www.facebook.com/tr?id=470401770439117&ev=PageView&noscript=1"
            alt={t('document.facebookPixelAlt')}
          />
        </noscript>
        <noscript>
          <Copy id="document.enableJavaScript" />
        </noscript>

        <div id="root">{children}</div>

        {/* window.__SSR_DATA__ must be identical on server and client so React
            does not see a hydration mismatch. suppressHydrationWarning is a
            belt-and-suspenders guard for any edge-case serialisation diff. */}
        <script suppressHydrationWarning dangerouslySetInnerHTML={{ __html: `window.__SSR_DATA__=${ssrDataStr}` }} />

        {/* Must run before styled-components initialises. Keeps CSSOM insertRule()
            mode (false = speedy enabled), avoiding full-stylesheet re-parse that
            drops rules for large component trees. */}
        <script dangerouslySetInnerHTML={{ __html: 'window.SC_DISABLE_SPEEDY = false' }} />
      </body>
    </html>
  )
}

export default Document
